import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Volume1, Volume2, VolumeX } from "lucide-react"
import { useDropdownMenu } from "@/components/ui/dropdown-menu-position"
import { cn } from "@/lib/utils"
import { useTranslation } from "@/hooks/use-translation"
import { trackVolume } from "./timeline-model"
import type { TimelineTrack } from "./types"

/** Шаг колеса мыши и клавиатурных стрелок, %. */
const WHEEL_STEP = 4
/** Уровень, до которого поднимаем громкость при размьюте «с нуля». */
const UNMUTE_TO = 60
/** Уровень ≤ этого порога трактуется как мьют (фейдер, дотянутый до низа, глушит дорожку). */
const MUTE_AT = 2
/** Коммиты во время жеста: живой звук без applyDoc на каждый шаг фейдера. */
const COMMIT_THROTTLE_MS = 150
/** Мост между кнопкой и панелью: панель в портале, наведение переезжает с зазором. */
const CLOSE_GRACE_MS = 120

const PANEL_WIDTH_PX = 40
const FADER_HEIGHT_PX = 130

type Props = {
  track: TimelineTrack
  onTrackAudioChange: (trackId: string, volume: number, muted: boolean) => void
}

/**
 * Громкость дорожки по дизайн-прототипу «Громкость дорожки»: клик по иконке — сразу мьют,
 * наведение (или фокус) выкатывает вертикальный фейдер под кнопкой, колесо мыши над иконкой
 * или фейдером меняет уровень. Уровень ≤2% = мьют; размьют с нуля восстанавливает 60%.
 * Панель — в портале: шапка и контейнер дорожек обрезают absolute-содержимое.
 */
export function TrackVolumeControl({ track, onTrackAudioChange }: Props) {
  const { t } = useTranslation(["shot"])
  const [open, setOpen] = useState(false)
  const [dragging, setDragging] = useState(false)
  // Локальное значение на время жеста: applyDoc коммитится раз в COMMIT_THROTTLE_MS,
  // а не на каждый pointermove (каждый коммит = ре-рендер всего редактора).
  const [draftPercent, setDraftPercent] = useState<number | null>(null)

  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const faderTrackRef = useRef<HTMLDivElement>(null)
  const openFlagsRef = useRef({ trigger: false, panel: false, dragging: false, focus: false })
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const commitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingRef = useRef<{ volume: number; muted: boolean } | null>(null)

  const percent = draftPercent ?? Math.round(trackVolume(track) * 100)
  const muted = draftPercent != null ? draftPercent <= MUTE_AT : track.muted
  const percentRef = useRef(percent)
  percentRef.current = percent

  const syncOpen = () => {
    const f = openFlagsRef.current
    const want = f.trigger || f.panel || f.dragging || f.focus
    if (want) {
      if (closeTimerRef.current != null) {
        clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }
      setOpen(true)
    } else if (closeTimerRef.current == null) {
      closeTimerRef.current = setTimeout(() => {
        closeTimerRef.current = null
        const g = openFlagsRef.current
        if (!(g.trigger || g.panel || g.dragging || g.focus)) setOpen(false)
      }, CLOSE_GRACE_MS)
    }
  }

  const flushPending = () => {
    if (commitTimerRef.current != null) {
      clearTimeout(commitTimerRef.current)
      commitTimerRef.current = null
    }
    if (pendingRef.current != null) {
      const { volume, muted: m } = pendingRef.current
      pendingRef.current = null
      onTrackAudioChange(track.id, volume, m)
    }
    setDraftPercent(null)
  }

  /** Жест (фейдер/колесо/клавиатура): уровень ≤2% мьютит, всё выше — снимает мьют. */
  const setLevel = (raw: number) => {
    const v = Math.max(0, Math.min(100, Math.round(raw)))
    setDraftPercent(v)
    pendingRef.current = { volume: v / 100, muted: v <= MUTE_AT }
    if (commitTimerRef.current != null) return
    commitTimerRef.current = setTimeout(() => {
      commitTimerRef.current = null
      if (pendingRef.current != null) {
        const { volume, muted: m } = pendingRef.current
        pendingRef.current = null
        onTrackAudioChange(track.id, volume, m)
      }
      setDraftPercent(null)
    }, COMMIT_THROTTLE_MS)
  }
  const setLevelRef = useRef(setLevel)
  setLevelRef.current = setLevel

  const toggleMute = () => {
    const cur = percentRef.current
    const nextMuted = !muted
    const nextPercent = !nextMuted && cur <= MUTE_AT ? UNMUTE_TO : cur
    if (commitTimerRef.current != null) {
      clearTimeout(commitTimerRef.current)
      commitTimerRef.current = null
    }
    pendingRef.current = null
    setDraftPercent(null)
    onTrackAudioChange(track.id, nextPercent / 100, nextMuted)
  }

  const handleFaderDown = (e: React.PointerEvent) => {
    const trackEl = faderTrackRef.current
    if (!trackEl) return
    e.preventDefault()
    const rect = trackEl.getBoundingClientRect()
    const apply = (clientY: number) => setLevel((1 - (clientY - rect.top) / rect.height) * 100)
    apply(e.clientY)
    openFlagsRef.current.dragging = true
    setDragging(true)
    syncOpen()
    const move = (ev: PointerEvent) => apply(ev.clientY)
    const up = () => {
      window.removeEventListener("pointermove", move)
      window.removeEventListener("pointerup", up)
      openFlagsRef.current.dragging = false
      setDragging(false)
      flushPending()
      syncOpen()
    }
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
  }

  const handleFaderKey = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 10 : WHEEL_STEP
    let next: number | null = null
    if (e.key === "ArrowUp" || e.key === "ArrowRight") next = percentRef.current + step
    else if (e.key === "ArrowDown" || e.key === "ArrowLeft") next = percentRef.current - step
    else if (e.key === "Home") next = 100
    else if (e.key === "End") next = 0
    if (next == null) return
    e.preventDefault()
    setLevel(next)
  }

  // Колесо над иконкой и над панелью: React-обработчик wheel пассивный, preventDefault
  // не сработает — вешаем нативные non-passive слушатели (как в прототипе).
  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      setLevelRef.current(percentRef.current + (e.deltaY < 0 ? WHEEL_STEP : -WHEEL_STEP))
    }
    const els: HTMLElement[] = []
    if (rootRef.current) els.push(rootRef.current)
    if (open && menuRef.current) els.push(menuRef.current)
    for (const el of els) el.addEventListener("wheel", onWheel, { passive: false })
    return () => {
      for (const el of els) el.removeEventListener("wheel", onWheel)
    }
  }, [open])

  // Незакоммиченный жест не должен потеряться при анмаунте шапки.
  useEffect(() => {
    return () => {
      if (closeTimerRef.current != null) clearTimeout(closeTimerRef.current)
      if (commitTimerRef.current != null) clearTimeout(commitTimerRef.current)
      if (pendingRef.current != null) onTrackAudioChange(track.id, pendingRef.current.volume, pendingRef.current.muted)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- только на анмаунт
  }, [])

  const { menuStyle, portalRoot } = useDropdownMenu({
    open,
    direction: "down",
    align: "left",
    anchorRef: buttonRef,
    menuRef,
    rootRef,
    resolveWidth: () => ({ width: PANEL_WIDTH_PX }),
    onOutsideClick: () => {
      openFlagsRef.current = { trigger: false, panel: false, dragging: false, focus: false }
      setOpen(false)
    },
    estimatedMenuHeightPx: 174,
    estimatedMenuWidthPx: PANEL_WIDTH_PX,
  })

  const icon =
    muted || percent <= MUTE_AT ? (
      <VolumeX className="h-3 w-3" />
    ) : percent < 45 ? (
      <Volume1 className="h-3 w-3" />
    ) : (
      <Volume2 className="h-3 w-3" />
    )

  const levelLabel = muted ? t("timeline.volumeOff") : `${percent}%`

  const panel =
    open && menuStyle ? (
      <div
        ref={menuRef}
        style={menuStyle}
        // Панель в портале, но React-события всплывают по дереву компонентов — без stop
        // pointerdown долетает до шапки дорожки и запускает её drag-n-drop.
        onPointerDown={(e) => e.stopPropagation()}
        onMouseEnter={() => {
          openFlagsRef.current.panel = true
          syncOpen()
        }}
        onMouseLeave={() => {
          openFlagsRef.current.panel = false
          syncOpen()
        }}
        className="flex flex-col items-center gap-[9px] rounded-lg border border-ui-border-strong bg-[#1c1c20] px-[3px] pt-[9px] pb-[11px] shadow-[0_20px_25px_-5px_rgba(0,0,0,0.5)]"
      >
        <span className="font-mono text-[10px] text-ui-text-secondary tabular-nums">{levelLabel}</span>
        <div
          role="slider"
          tabIndex={0}
          aria-label={t("timeline.trackVolume")}
          aria-orientation="vertical"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          aria-valuetext={levelLabel}
          onPointerDown={handleFaderDown}
          onKeyDown={handleFaderKey}
          onFocus={() => {
            openFlagsRef.current.focus = true
            syncOpen()
          }}
          onBlur={() => {
            openFlagsRef.current.focus = false
            syncOpen()
          }}
          className="relative flex h-[130px] w-8 cursor-ns-resize touch-none justify-center rounded focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none"
        >
          <div
            ref={faderTrackRef}
            className="relative w-1.5 rounded-full bg-ui-surface-sunken"
            style={{ height: FADER_HEIGHT_PX }}
          >
            <div
              className="absolute inset-x-0 bottom-0 rounded-full"
              style={{
                height: `${percent}%`,
                background: muted ? "var(--ui-text-faint)" : "var(--ui-accent)",
                transition: dragging ? "none" : "height .12s ease-out, background .15s",
              }}
            />
            <div
              className="absolute left-1/2 h-3.5 w-3.5 rounded-full"
              style={{
                bottom: `${percent}%`,
                margin: "0 0 -7px -7px",
                background: muted ? "var(--ui-text-muted)" : "var(--ui-accent-hover)",
                boxShadow: dragging
                  ? "0 0 0 5px rgba(139,92,246,.18), 0 1px 4px rgba(0,0,0,.6)"
                  : "0 1px 4px rgba(0,0,0,.6)",
                transition: dragging ? "box-shadow .12s" : "bottom .12s ease-out, background .15s, box-shadow .12s",
              }}
            />
          </div>
        </div>
      </div>
    ) : null

  return (
    <div
      ref={rootRef}
      className="relative inline-flex leading-none"
      onMouseEnter={() => {
        openFlagsRef.current.trigger = true
        syncOpen()
      }}
      onMouseLeave={() => {
        openFlagsRef.current.trigger = false
        syncOpen()
      }}
    >
      <button
        type="button"
        ref={buttonRef}
        title={muted ? t("timeline.unmuteTrack") : t("timeline.muteTrack")}
        aria-label={muted ? t("timeline.unmuteTrack") : t("timeline.muteTrack")}
        aria-pressed={muted}
        onClick={toggleMute}
        onFocus={(e) => {
          // Фейдер должен открываться и с клавиатуры, не только по наведению.
          if (e.currentTarget.matches(":focus-visible")) {
            openFlagsRef.current.focus = true
            syncOpen()
          }
        }}
        onBlur={() => {
          openFlagsRef.current.focus = false
          syncOpen()
        }}
        className={cn(
          "rounded p-1 transition-colors",
          muted
            ? "bg-white/[0.07] text-ui-text-faint"
            : "text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary",
        )}
      >
        {icon}
      </button>
      {panel && portalRoot ? createPortal(panel, portalRoot) : null}
    </div>
  )
}
